/**
 * DashboardSimple - Simpel, garanteret virkende dashboard
 * Bygget fra bunden med minimal kompleksitet
 */

import { useState, useEffect } from 'react'
import { getCurrentUser, onAuthChange } from '../services/firebaseService'
import { collection, query, where, getDocs, deleteDoc, doc } from 'firebase/firestore'
import { db } from '../config/firebase'
import '../styles/pages/dashboard.css'

export default function DashboardSimple() {
  const [user, setUser] = useState(null)
  const [evaluations, setEvaluations] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedEvaluation, setSelectedEvaluation] = useState(null)

  useEffect(() => {
    // Check user immediately
    const currentUser = getCurrentUser()
    setUser(currentUser)
    
    if (currentUser) {
      loadEvaluations(currentUser.uid)
    } else {
      setLoading(false)
    }
    
    // Listen to auth changes
    const unsubscribe = onAuthChange((authUser) => {
      setUser(authUser)
      if (authUser) {
        loadEvaluations(authUser.uid)
      } else {
        setEvaluations([])
        setLoading(false)
      }
    })
    
    return () => unsubscribe()
  }, [])

  const loadEvaluations = async (userId) => {
    try {
      setLoading(true)
      console.log('🔍 Loading evaluations for userId:', userId)
      
      // SIMPEL QUERY - ingen orderBy, ingen kompleksitet
      const q = query(
        collection(db, 'evaluations'),
        where('userId', '==', userId)
      )
      
      const snapshot = await getDocs(q)
      console.log('✅ Found', snapshot.size, 'evaluations')
      
      const evaluationsList = []
      snapshot.forEach((docSnapshot) => {
        const data = docSnapshot.data()
        
        // Håndter createdAt - SIMPEL måde
        let createdAt = new Date()
        if (data.createdAt) {
          if (data.createdAt.toDate) {
            // Firestore Timestamp
            createdAt = data.createdAt.toDate()
          } else if (data.createdAt instanceof Date) {
            // Allerede Date
            createdAt = data.createdAt
          } else if (typeof data.createdAt === 'string' || typeof data.createdAt === 'number') {
            // String eller timestamp
            createdAt = new Date(data.createdAt)
          }
        }
        
        evaluationsList.push({
          id: docSnapshot.id,
          ...data,
          createdAt: createdAt
        })
      })
      
      // Sort manuelt (nyeste først)
      evaluationsList.sort((a, b) => {
        return b.createdAt - a.createdAt
      })
      
      console.log('📊 Evaluations loaded:', evaluationsList.length)
      console.log('📋 Sample evaluation:', evaluationsList[0])
      
      setEvaluations(evaluationsList)
    } catch (error) {
      console.error('❌ Error loading evaluations:', error)
      alert('Kunne ikke indlæse evalueringer: ' + error.message)
    } finally {
      setLoading(false)
    }
  }

  const deleteEvaluation = async (evaluationId) => {
    if (!confirm('Er du sikker på at du vil slette denne evaluering?')) {
      return
    }

    try {
      await deleteDoc(doc(db, 'evaluations', evaluationId))
      setEvaluations(prev => prev.filter(e => e.id !== evaluationId))
      if (selectedEvaluation?.id === evaluationId) {
        setSelectedEvaluation(null)
      }
      console.log('✅ Evaluation deleted')
    } catch (error) {
      console.error('❌ Error deleting:', error)
      alert('Fejl ved sletning: ' + error.message)
    }
  }

  // Not logged in
  if (!user) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-content">
          <div style={{ textAlign: 'center', padding: '60px 20px' }}>
            <div style={{ fontSize: '3em', marginBottom: '16px' }}>🔒</div>
            <h2>Du skal være logget ind</h2>
            <p style={{ marginBottom: '24px', color: 'var(--text-secondary)' }}>
              Log ind for at se dit dashboard og gemte evalueringer.
            </p>
            <button className="btn" onClick={() => window.location.href = '/dist/'}>
              Gå til forsiden og log ind
            </button>
          </div>
        </div>
      </div>
    )
  }

  // Loading
  if (loading) {
    return (
      <div className="dashboard-page">
        <div className="dashboard-content">
          <h2>📊 Dashboard</h2>
          <p>Indlæser evalueringer...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="dashboard-page">
      <div className="dashboard-content">
        <div className="dashboard-header">
          <h2>📊 Dit Dashboard</h2>
          <div className="dashboard-stats">
            <div className="stat-card">
              <div className="stat-value">{evaluations.length}</div>
              <div className="stat-label">Gemte Evalueringer</div>
            </div>
          </div>
        </div>

        {evaluations.length === 0 ? (
          <div className="empty-state">
            <div style={{ fontSize: '3em', marginBottom: '16px' }}>📋</div>
            <h3>Ingen evalueringer endnu</h3>
            <p>Når du laver evalueringer, kan de gemmes her.</p>
            <button className="btn" onClick={() => window.location.href = '/dist/?step=preferences'}>
              Lav dit første kort →
            </button>
          </div>
        ) : (
          <div className="dashboard-grid">
            <div className="evaluations-list">
              <h3>Gemte Evalueringer</h3>
              {evaluations.map((evaluation) => (
                <div
                  key={evaluation.id}
                  className={`evaluation-card ${selectedEvaluation?.id === evaluation.id ? 'selected' : ''}`}
                  onClick={() => setSelectedEvaluation(evaluation)}
                >
                  <div className="evaluation-header">
                    <h4>{evaluation.title || 'Evaluering'}</h4>
                    <button
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation()
                        deleteEvaluation(evaluation.id)
                      }}
                    >
                      🗑️
                    </button>
                  </div>
                  <div className="evaluation-meta">
                    <span>{evaluation.createdAt.toLocaleDateString('da-DK', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric'
                    })}</span>
                    {evaluation.overallMatch && (
                      <span className="match-score">
                        Match: {(evaluation.overallMatch * 100).toFixed(0)}%
                      </span>
                    )}
                  </div>
                  {evaluation.modules && evaluation.modules.length > 0 && (
                    <div className="evaluation-modules">
                      {evaluation.modules.slice(0, 3).map((mod, i) => (
                        <span key={i} className="module-tag">{mod}</span>
                      ))}
                      {evaluation.modules.length > 3 && (
                        <span className="module-tag">+{evaluation.modules.length - 3}</span>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {selectedEvaluation && (
              <div className="evaluation-detail">
                <div className="detail-header">
                  <h3>{selectedEvaluation.title || 'Evaluering Detaljer'}</h3>
                  <button className="close-btn" onClick={() => setSelectedEvaluation(null)}>
                    ×
                  </button>
                </div>
                <div className="detail-content">
                  {/* Match Results */}
                  {selectedEvaluation.matchResults && (
                    <div className="detail-section">
                      <h4>Match Oversigt</h4>
                      {selectedEvaluation.matchResults.overallMatch && (
                        <div className="overall-match">
                          <div className="match-value">
                            {(selectedEvaluation.matchResults.overallMatch * 100).toFixed(0)}%
                          </div>
                          <div className="match-label">Overall Match</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* General Feedback */}
                  {selectedEvaluation.generalFeedback && (
                    <div className="detail-section">
                      <h4>📊 Din Evaluering</h4>
                      {selectedEvaluation.generalFeedback.summary && (
                        <div style={{ 
                          padding: '16px', 
                          background: 'rgba(102, 126, 234, 0.1)', 
                          borderRadius: '8px',
                          marginBottom: '16px'
                        }}>
                          <p style={{ margin: 0, lineHeight: '1.7' }}>
                            {selectedEvaluation.generalFeedback.summary}
                          </p>
                        </div>
                      )}
                      
                      {selectedEvaluation.generalFeedback.insights && 
                       selectedEvaluation.generalFeedback.insights.length > 0 && (
                        <div style={{ marginBottom: '16px' }}>
                          <h5>💡 Insigter</h5>
                          {selectedEvaluation.generalFeedback.insights.map((insight, index) => (
                            <div key={index} style={{
                              padding: '12px',
                              marginBottom: '8px',
                              background: insight.type === 'strength' 
                                ? 'rgba(81, 207, 102, 0.1)' 
                                : 'rgba(255, 193, 7, 0.1)',
                              borderRadius: '8px',
                              border: `1px solid ${insight.type === 'strength' ? 'rgba(81, 207, 102, 0.3)' : 'rgba(255, 193, 7, 0.3)'}`
                            }}>
                              <strong>{insight.title}</strong>
                              <p style={{ margin: '4px 0 0 0', fontSize: '0.9em' }}>
                                {insight.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      )}
                      
                      {selectedEvaluation.generalFeedback.actionItems && 
                       selectedEvaluation.generalFeedback.actionItems.length > 0 && (
                        <div>
                          <h5>🎯 Næste Skridt</h5>
                          {selectedEvaluation.generalFeedback.actionItems.map((item, index) => (
                            <div key={index} style={{
                              padding: '12px',
                              marginBottom: '8px',
                              background: 'rgba(102, 126, 234, 0.1)',
                              borderRadius: '8px',
                              borderLeft: `4px solid ${item.priority === 'high' ? '#ff6b6b' : item.priority === 'medium' ? '#FFC107' : 'var(--cyber-blue)'}`
                            }}>
                              <p style={{ margin: 0, fontSize: '0.9em' }}>{item.text}</p>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="dashboard-actions">
          <button className="btn" onClick={() => window.location.href = '/dist/?step=preferences'}>
            ➕ Lav nyt kort
          </button>
          <button className="btn btn-secondary" onClick={() => window.location.href = '/dist/'}>
            ← Tilbage til forsiden
          </button>
        </div>
      </div>
    </div>
  )
}
